import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=1600&q=80";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const yPath = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacityText = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lineReveal = {
    hidden: { y: "110%" },
    show: (i) => ({
      y: 0,
      transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 * i + 0.3 },
    }),
  };

  return (
    <section ref={ref} className="relative min-h-screen bg-[#0d1b3e] text-white overflow-hidden" data-testid="hero">
      {/* Parallax background photo, right side clipped frame */}
      <motion.div
        style={{ y: yImg, scale: scaleImg }}
        className="absolute top-0 right-0 h-full w-[62%] hidden md:block"
      >
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b3e] via-[#0d1b3e]/40 to-[#0d1b3e]/10" />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(180deg, rgba(13,27,62,0.4) 0%, transparent 30%, transparent 70%, rgba(13,27,62,0.85) 100%)"
        }} />
      </motion.div>

      {/* Decorative parallax path SVG */}
      <motion.svg
        style={{ y: yPath }}
        className="absolute bottom-0 left-0 w-full h-1/2 pointer-events-none opacity-40"
        viewBox="0 0 1440 500" preserveAspectRatio="none"
      >
        <motion.path
          d="M0 480 C 200 400, 300 460, 480 380 C 640 310, 780 350, 940 260 C 1100 175, 1220 210, 1440 120"
          fill="none" stroke="#b8892b" strokeWidth="1.5"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 3, ease: [0.22, 1, 0.36, 1], delay: 1 }}
        />
      </motion.svg>

      {/* Grid noise */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }} />

      <motion.div style={{ opacity: opacityText }} className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-32 md:pt-44 pb-20 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-6 md:mb-8"
        >
          <span className="rule-gold" />
          <span className="eyebrow text-[#b8892b]">01 · Discover · Study in France</span>
        </motion.div>

        <h1 className="serif font-light text-[11.5vw] md:text-[9.5vw] lg:text-[8.2vw] leading-[0.95] tracking-[-0.03em] max-w-6xl">
          {["Study in France", "without the stress."].map((line, i) => (
            <span key={i} className="mask-line block">
              <motion.span
                custom={i} variants={lineReveal} initial="hidden" animate="show"
                className="block"
                style={{ color: i === 1 ? "#b8892b" : "#ffffff", fontStyle: i === 1 ? "italic" : "normal" }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="grid md:grid-cols-12 gap-6 mt-12 max-w-5xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="md:col-span-7 text-lg md:text-xl leading-relaxed text-white/80"
          >
            FrancePath helps American students and families find the right university, complete applications, secure housing, prepare visas, discover part-time work opportunities, and confidently begin life in France.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="md:col-span-5 flex flex-wrap items-start gap-3"
          >
            <Link to="/advisor" className="btn-sharp btn-sharp-gold" data-testid="hero-cta-primary">
              Get My Free Study Plan <ArrowUpRight size={16} />
            </Link>
            <a href="https://wa.me/33618797689" target="_blank" rel="noreferrer" className="btn-sharp btn-sharp-outline !text-white !border-white/40 hover:!bg-white hover:!text-[#0d1b3e]"
              data-testid="hero-cta-secondary">
              Book a free consultation
            </a>
          </motion.div>
        </div>

        {/* Value prop above-the-fold */}
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-10 md:mt-14 max-w-3xl serif italic text-white/70 text-xl md:text-2xl leading-snug"
          data-testid="hero-value-prop"
        >
          Most students can complete a bachelor's degree in France for less than one year at many private U.S. universities.
        </motion.p>

        {/* Kicker stats row */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 1 }}
          className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-white/10 pt-8"
        >
          {[
            ["€2,895", "Average public university tuition"],
            ["3 years", "Typical bachelor's degree"],
            ["~$14k", "Estimated first-year cost"],
            ["100%", "Every major step mapped"],
          ].map(([n, l], i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="serif text-3xl md:text-4xl text-[#b8892b]">{n}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-2">{l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
